import React, { useCallback } from 'react';
import ValidInputText, { ValidPropText } from '../../../../core/components/validation/ValidInputText';
import ValidInputRadioText from '../../../../core/components/validation/ValidInputRadioText';
import ValidInputDroplistText from '../../../../core/components/validation/ValidInputDroplistText';
import { ModelProp, StatusVal } from '../payor_edit_types';
import { usePayorEditStore } from '../pagehooks/usePayorEditStore';
import usePayorEditFormInit from './usePayorEditFormInit';
import { usePayorEditState } from './usePayorEditState';
import usePayorEditModel from './usePayorEditModel';
import Button from '../../../../core/components/Button';
import FormEdit from '../../../../core/components/form/FormEdit';
import FormField from '../../../../core/components/form/FormField';
import FormSubmit from '../../../../core/components/form/FormSubmit';

const PayorEditForm = (): JSX.Element => {
    usePayorEditFormInit();
    const { init } = usePayorEditState();

    // wait for init
    if (init) {
        return <></>;
    }

    return (
        <FormEdit
            formName="grpEditAcct"
            cssName="grp-acct"
        >
            <EditModel />
        </FormEdit>
    );
};

const EditModel = (): JSX.Element => {
    const { state: pageState, cancelEdit } = usePayorEditStore();
    const { model, setPropText, submit, setStatus } = usePayorEditModel();

    const isUPDATE = pageState.mode == 'UPDATE';
    const readonly = !pageState.canEdit || model.Status.val == 'INACTIVE';
    const readonlyBinPcnGrp = readonly || isUPDATE;
    const showSubmit = !readonly;
    const disableStatusButtons = !pageState.canEdit || pageState.mode == 'CREATE';

    const handlePropChange = useCallback(
        (prop: string, val: string | number) => setPropText(prop as ModelProp, `${val}`),
        [setPropText],
    );

    return (
        <>
            {isUPDATE ? (
                <InputStatus
                    prop={model.Status}
                    disabled={disableStatusButtons}
                    onChange={id => setStatus(id)}
                />
            ) : null}
            <ValidInputRadioText
                prop={model.MedType}
                disabled={readonly}
                onChange={handlePropChange}
            />
            <ValidInputDroplistText
                prop={model.State}
                disabled={readonly}
                onChange={handlePropChange}
            />
            <ValidInputText
                prop={model.BIN}
                maxlen={6}
                placeholder="bin"
                disabled={readonlyBinPcnGrp}
                onChange={handlePropChange}
            />
            <ValidInputText
                prop={model.PCN}
                maxlen={20}
                placeholder="pcn"
                disabled={readonlyBinPcnGrp}
                onChange={handlePropChange}
            />
            <ValidInputText
                prop={model.GroupNumber}
                maxlen={20}
                placeholder="group number"
                disabled={readonlyBinPcnGrp}
                onChange={handlePropChange}
            />
            <ValidInputText
                prop={model.Name}
                maxlen={100}
                width={240}
                placeholder="description"
                disabled={readonly}
                onChange={handlePropChange}
            />
            {isUPDATE ? (
                <>
                    <FormField>
                        <label className="same-width">Created By</label>
                        {!!model.CreatedBy && (
                            <label>
                                {model.CreatedBy} on {model.CreatedDate}
                            </label>
                        )}
                    </FormField>
                    <FormField>
                        <label className="same-width">Last Modified</label>
                        {!!model.ModifiedBy && (
                            <label>
                                {model.ModifiedBy} on {model.ModifiedDate}
                            </label>
                        )}
                    </FormField>
                </>
            ) : null}
            {showSubmit ? (
                <FormSubmit lbl="Apply Changes">
                    <Button onClick={submit}>{pageState.mode}</Button>
                    <span>
                        <Button
                            cssName="btn-cancel"
                            onClick={cancelEdit}
                        >
                            CANCEL
                        </Button>
                    </span>
                </FormSubmit>
            ) : null}
        </>
    );
};

export type InputStatusParams = {
    prop: ValidPropText;
    disabled?: boolean;
    onChange: (id: StatusVal) => void;
};
export const InputStatus = ({ prop, disabled, onChange }: InputStatusParams): JSX.Element => {
    return (
        <div className="form-field input-status-change">
            <label className="same-width">Status</label>
            <button disabled={true}>{prop.val}</button>
            {disabled ? null : (
                <>
                    <span> Switch Status to:</span>
                    {prop.val == 'ACTIVE' && (
                        <span>
                            <Button
                                id={prop.key + ' INACTIVE'}
                                cssName="btn-cancel"
                                onClick={() => onChange('INACTIVE')}
                            >
                                INACTIVE
                            </Button>
                        </span>
                    )}
                    {prop.val == 'INACTIVE' && (
                        <span>
                            <Button
                                id={prop.key + ' ACTIVE'}
                                cssName="btn-alt"
                                onClick={() => onChange('ACTIVE')}
                            >
                                ACTIVE
                            </Button>
                        </span>
                    )}
                </>
            )}
        </div>
    );
};

export default PayorEditForm;
